// Client-side recording observability — captured on session start and sent
// alongside /transcription/start so we can correlate blank-transcript /
// corruption failures with the specific browser, extension version, mic
// device, network state, and app build that produced the audio.
//
// Persisted as a single `client_metadata` JSONB column on transcript_sections.
// Shape is enforced here in TS, not at the DB layer, so adding a new field
// requires only TS changes — no migration. See migration
// `20260527165708_transcript_sections_client_metadata.sql`.

export type RecorderSource = "web_app" | "chrome_extension" | "ios" | "android";

// Internal audio-capture transport, independent of the UI context the user was
// in. When the extension iframe loads the web app but the extension API isn't
// available (provider race, older extension), the recording falls back to the
// browser's MediaRecorder while the user is still visually inside the
// extension. The `recorder_source` field then says `chrome_extension`
// (because that's where the user is) but `recording_transport` says
// `browser` (because that's what handled the audio). Both are forensically
// useful and answer different questions.
export type RecordingTransport = "extension" | "browser";

// Synchronous, idempotent detection of whether we're running inside the
// Chrome-Ext-V2 sidebar iframe. The extension's content script injects an
// <iframe src="https://app.gohappydoc.com/?sidebar=true"> on PIMS pages
// (Chrome-Ext-V2/src/contentScript/sidebar/Sidebar.ts +
// src/config/index.ts#defaultUrl). We detect this by checking BOTH:
//   1. The `sidebar=true` URL param the extension itself appends. Same-origin
//      read, available before any provider handshake settles, immune to the
//      ChromeExtensionProvider initialisation race.
//   2. We're actually inside an iframe (`window.parent !== window`). Without
//      this AND-clause, a user who navigated to ?sidebar=true in a normal tab
//      would be mis-tagged as extension.
// `isSidebarWindow` from ChromeExtensionProvider depends on a postMessage
// handshake that V2 never sends — that path is reserved for a future native
// side_panel build and is dead in production today. We still honour it as a
// secondary signal so when that build ships, we keep tagging correctly.
const detectInExtensionIframe = (): boolean => {
    if (typeof window === "undefined") return false;
    try {
        if (window.parent === window) return false;
        const params = new URLSearchParams(window.location.search);
        if (params.get("sidebar") === "true") return true;
    } catch {
        // location.search read can fail in exotic sandboxes — fail closed.
    }
    return false;
};

// Lazy memo — locks on first call IN A REAL BROWSER so the verdict is stable
// for the lifetime of the SPA. We defer module-load evaluation so tests can
// stub `window.location` / `window.parent` between imports without
// module-registry tricks (see __resetIframeDetectionForTests below), and we
// skip caching when window is undefined (SSR / Node test runner with no JSDOM)
// so a pre-hydration probe doesn't permanently poison the cache to `false`
// before the real browser context is available.
let _cachedInExtensionIframe: boolean | undefined;
export const isRunningInExtensionIframe = (): boolean => {
    if (typeof window === "undefined") return false;
    if (_cachedInExtensionIframe === undefined) {
        _cachedInExtensionIframe = detectInExtensionIframe();
    }
    return _cachedInExtensionIframe;
};

// Test-only escape hatch — production code never calls this. Lets specs reset
// the iframe-detection cache between cases that mock `window.parent` differently.
export const __resetIframeDetectionForTests = (): void => {
    _cachedInExtensionIframe = undefined;
};

// `AudioInputDevice` is intentionally label-only — see
// `captureAudioInputDevices` for the reasoning (persistent hardware
// fingerprints don't belong on every row of the full enumerated list).
export interface AudioInputDevice {
    label: string;
}

// Label only. `deviceId`/`groupId` are persistent per-origin identifiers
// (W3C MediaCapture §3.3 treats them as cookie-like) and we don't need them
// — the human-readable label ("Built-in Microphone", "USB Headset") is what
// answers the actual forensic question.
export interface MicDevice {
    label: string;
}

export type WakeLockStatus = "acquired" | "denied" | "unsupported" | "released_by_browser";

// EN-1383: the page-level current wake lock status (useScreenWakeLock has no session id). It is copied
// into the session that is active when it changes, and into each new session at capture (so an hourly
// split inherits it). Each session then keeps its own snapshot, so a recording that is still uploading
// is not given the status of the next recording. The session patch lives below `sessions`.
let _wakeLockStatus: WakeLockStatus | null = null;
export const getWakeLockStatus = (): WakeLockStatus | null => _wakeLockStatus;

export interface ClientRecordingMetadata {
    // Tier 0 — source identity
    recorder_source: RecorderSource | null;
    recording_transport: RecordingTransport | null;
    mic_device: MicDevice | null;

    // Tier 1 — high-signal partitioning
    app_version: string | null;
    extension_version: string | null;
    extension_id: string | null;
    mime_type: string | null;
    online_status: boolean | null;
    connection_type: string | null;
    is_sidebar_window: boolean | null;

    // Tier 2 — mid-signal forensics
    pause_resume_supported: boolean | null;
    user_agent: string | null;
    audio_settings: Record<string, unknown> | null;
    audio_input_count: number | null;
    audio_input_devices: AudioInputDevice[] | null;

    // Tier 3 — environment baseline
    tab_visibility_at_start: string | null;
    audio_context_sample_rate: number | null;
    screen_resolution: string | null;
    device_pixel_ratio: number | null;
    max_touch_points: number | null;

    // EN-1383: wake lock outcome while this session was active, and how many times the browser
    // released the lock. Both absent when the lock was never attempted (desktop).
    wake_lock_status?: WakeLockStatus;
    wake_lock_release_count?: number;
}

const emptyMetadata = (): ClientRecordingMetadata => ({
    recorder_source: null,
    recording_transport: null,
    mic_device: null,
    app_version: null,
    extension_version: null,
    extension_id: null,
    mime_type: null,
    online_status: null,
    connection_type: null,
    is_sidebar_window: null,
    pause_resume_supported: null,
    user_agent: null,
    audio_settings: null,
    audio_input_count: null,
    audio_input_devices: null,
    tab_visibility_at_start: null,
    audio_context_sample_rate: null,
    screen_resolution: null,
    device_pixel_ratio: null,
    max_touch_points: null,
});

// Session-keyed metadata. Was a single shared singleton in earlier revisions —
// but during browser-transport hour rollover, `mediaRecorder.stop()` queues
// `ondataavailable` asynchronously while `startRecording()` for the next hour
// runs synchronously and resets the singleton. By the time the prior session's
// TUS upload + `transcribeAudio()` reads `getClientMetadata()`, it gets the
// new session's environment, mis-attributing the prior recording. Keying by
// `transcriptSectionId` (a stable per-session ID generated before recording
// starts) keeps each session's snapshot isolated regardless of arming order.
const sessions = new Map<string, ClientRecordingMetadata>();

// The session new wake lock events belong to: the most recently captured one.
let activeSessionId: string | null = null;

const ensure = (sessionId: string): ClientRecordingMetadata => {
    let m = sessions.get(sessionId);
    if (!m) {
        m = emptyMetadata();
        sessions.set(sessionId, m);
    }
    return m;
};

const patch = (sessionId: string, partial: Partial<ClientRecordingMetadata>): void => {
    sessions.set(sessionId, { ...ensure(sessionId), ...partial });
};

// Returns null for an unknown sessionId so callers can distinguish
// "telemetry never armed" from "telemetry armed but everything was null".
export const getClientMetadata = (sessionId: string): ClientRecordingMetadata | null => sessions.get(sessionId) ?? null;

// Updates the page-level status and the active session's snapshot. `null` (a new activation) removes the
// session's wake lock keys. Never recreates a session entry that was already cleared.
export const setWakeLockStatus = (status: WakeLockStatus | null): void => {
    _wakeLockStatus = status;
    const session = activeSessionId ? sessions.get(activeSessionId) : undefined;
    if (!activeSessionId || !session) return;
    if (status === null) {
        const { wake_lock_status: _s, wake_lock_release_count: _c, ...rest } = session;
        void _s;
        void _c;
        sessions.set(activeSessionId, rest);
        return;
    }
    sessions.set(activeSessionId, {
        ...session,
        wake_lock_status: status,
        wake_lock_release_count: session.wake_lock_release_count ?? 0,
    });
};

// A browser-initiated release (not our own). Counted on the active session.
export const recordWakeLockRelease = (): void => {
    const session = activeSessionId ? sessions.get(activeSessionId) : undefined;
    if (!activeSessionId || !session) return;
    sessions.set(activeSessionId, {
        ...session,
        wake_lock_release_count: (session.wake_lock_release_count ?? 0) + 1,
    });
};

// Drop the entry after the upload has finalised. Page-lifetime growth is
// trivial (~1KB/session) but explicit cleanup keeps the dev experience clean
// and makes long-lived tabs (sales demos, training) predictable.
export const clearClientMetadata = (sessionId: string): void => {
    sessions.delete(sessionId);
    if (activeSessionId === sessionId) activeSessionId = null;
};

// Reads the frontend bundle version. Source-of-truth is `app_versions` in
// localStorage, written by AppVersionGuard on boot. Duplicates the
// implementation in main.tsx#getAppVersionFromStorage intentionally so this
// module has no React entry-point dependency.
const readAppVersionFromStorage = (): string | null => {
    if (typeof localStorage === "undefined") return null;
    try {
        const appVersions = localStorage.getItem("app_versions");
        if (!appVersions) return null;
        const parsed = JSON.parse(appVersions);
        return parsed.version?.toString() ?? null;
    } catch {
        return null;
    }
};

export interface CaptureEnvironmentOptions {
    // `transport` is the actual audio-capture code path being entered. The
    // caller knows this unambiguously. `recorder_source` is then DERIVED:
    //   - extension transport → always "chrome_extension"
    //   - browser transport → "chrome_extension" if we're inside the extension's
    //     iframe (so the user is visually inside the extension UI even though
    //     the API fell back), otherwise "web_app".
    // This is the only place this derivation happens; callers don't pick
    // recorder_source manually.
    transport: RecordingTransport;
    mimeType: string | null;
    extensionVersion: string | null;
    extensionId: string | null;
    isSidebarWindow: boolean | null;
    pauseResumeSupported: boolean | null;
}

const deriveRecorderSource = (transport: RecordingTransport): RecorderSource => {
    if (transport === "extension") return "chrome_extension";
    return isRunningInExtensionIframe() ? "chrome_extension" : "web_app";
};

// Synchronous browser-environment snapshot. Call this at the top of each
// transport entry path (before getUserMedia / startAudioCapture) with the
// session's transcriptSectionId. Replaces any existing entry for that
// sessionId, then progressively patches more data as it becomes available
// (mic_device after getUserMedia, audio_context_sample_rate after AudioContext).
export const captureSessionEnvironment = (sessionId: string, opts: CaptureEnvironmentOptions): void => {
    sessions.set(sessionId, emptyMetadata());
    activeSessionId = sessionId;
    const nav = typeof navigator !== "undefined" ? navigator : undefined;
    // Network Information API is non-standard; not exposed by Firefox/Safari.
    const connection = (nav as (Navigator & { connection?: { effectiveType?: string } }) | undefined)?.connection;

    // `is_sidebar_window` should answer "was the user inside the extension's
    // sidebar context?". ChromeExtensionProvider's `isSidebarWindow` reads
    // from a postMessage handshake that V2 never sends (it's reserved for a
    // future native side_panel build), so trusting opts.isSidebarWindow alone
    // produces `null`/`false` for every V2 user — useless for partitioning.
    // OR with the URL+iframe check so the field is true for today's V2
    // sidebar AND for any future native side_panel build.
    const inIframe = isRunningInExtensionIframe();
    const sidebarWindow = opts.isSidebarWindow === true || inIframe;

    patch(sessionId, {
        recorder_source: deriveRecorderSource(opts.transport),
        recording_transport: opts.transport,
        app_version: readAppVersionFromStorage(),
        extension_version: opts.extensionVersion,
        extension_id: opts.extensionId,
        mime_type: opts.mimeType,
        online_status: nav ? nav.onLine : null,
        connection_type: connection?.effectiveType ?? null,
        is_sidebar_window: sidebarWindow,
        pause_resume_supported: opts.pauseResumeSupported,
        user_agent: nav?.userAgent ?? null,
        tab_visibility_at_start: typeof document !== "undefined" ? document.visibilityState : null,
        screen_resolution: typeof screen !== "undefined" ? `${screen.width}x${screen.height}` : null,
        device_pixel_ratio: typeof window !== "undefined" ? window.devicePixelRatio : null,
        max_touch_points: nav?.maxTouchPoints ?? null,
        ...(_wakeLockStatus !== null ? { wake_lock_status: _wakeLockStatus, wake_lock_release_count: 0 } : {}),
    });
};

// Patches mic_device + audio_settings from a live MediaStream. Both come
// from the same `MediaStreamTrack.getSettings()` object so we read once.
// Browser-transport only — extension recordings own their stream in the
// offscreen doc and don't expose track settings.
//
// `deviceId` and `groupId` are stripped from both `mic_device` and
// `audio_settings` before persistence — they're persistent per-origin
// identifiers (W3C MediaCapture §3.3) and the human-readable `label` carries
// the forensic signal we actually want.
export const captureActiveMicAndSettings = (sessionId: string, stream: MediaStream): void => {
    const [track] = stream.getAudioTracks();
    if (!track) {
        patch(sessionId, { mic_device: null, audio_settings: null });
        return;
    }
    const settings = track.getSettings();
    const { deviceId: _omitDeviceId, groupId: _omitGroupId, ...safeSettings } = settings;
    void _omitDeviceId;
    void _omitGroupId;
    patch(sessionId, {
        mic_device: { label: track.label ?? "" },
        audio_settings: safeSettings as Record<string, unknown>,
    });
};

// Hard timeout race for enumerateDevices. Slow machines (Windows with many
// virtual audio drivers, Bluetooth devices, OBS, VoiceMeeter) can take
// >500ms; we'd rather record without device enumeration than make the user
// wait. The caller awaits with this bound so very-short recordings still
// get at least the synchronous environment snapshot.
const ENUMERATE_DEVICES_TIMEOUT_MS = 200;

// `deviceId` and `groupId` are intentionally NOT persisted for the full
// device list — only for `mic_device` (the active mic) where they let us
// correlate failures to a specific physical device. Storing a per-clinician
// fingerprint of every enrolled USB headset on every transcript row is more
// retention than the forensic question justifies, especially in a
// HIPAA-adjacent context. `audio_input_count` plus `label` is enough to
// answer "did they have other inputs available?" and "what kind of mic?".
export const captureAudioInputDevices = async (sessionId: string): Promise<void> => {
    try {
        if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) return;
        const devices = await Promise.race([
            navigator.mediaDevices.enumerateDevices(),
            new Promise<MediaDeviceInfo[]>((_, reject) =>
                setTimeout(() => reject(new Error("enumerateDevices timeout")), ENUMERATE_DEVICES_TIMEOUT_MS),
            ),
        ]);
        const inputs = devices.filter((d) => d.kind === "audioinput").map((d) => ({ label: d.label }));
        patch(sessionId, {
            audio_input_count: inputs.length,
            audio_input_devices: inputs,
        });
    } catch {
        // best-effort
    }
};

export const setAudioContextSampleRate = (sessionId: string, rate: number | null): void => {
    patch(sessionId, { audio_context_sample_rate: rate });
};

// Standardised `extra` payload for `Sentry.captureException` calls tagged
// errorCategory:recording. We attach the full session blob — the same shape
// stored on the DB row — so a Sentry event is self-sufficient for triage
// without cross-referencing Supabase by transcriptSectionId.
export interface RecordingErrorContext {
    recordingState?: unknown;
    recordingTime?: number;
    activeRecordingId?: string;
    mediaRecorderState?: string;
    audioContextState?: string;
    transcriptSectionId?: string | null;
    mimeType?: string;
    [key: string]: unknown;
}

export const buildRecordingErrorExtra = (context: RecordingErrorContext = {}): Record<string, unknown> => {
    // sessionId is derived from context.transcriptSectionId — every recording
    // error site already includes it. If the session hasn't armed yet (e.g.
    // start_missing_patient_record fires before captureSessionEnvironment),
    // we attach `null` rather than fabricating empty fields.
    const sessionId = typeof context.transcriptSectionId === "string" ? context.transcriptSectionId : null;
    const m = sessionId ? sessions.get(sessionId) : undefined;
    return {
        ...context,
        clientMetadata: m ?? null,
    };
};
